import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  CheckCircle2, 
  Clock,
  Crown,
  Briefcase,
  Flag,
  Radio,
  Heart,
  Star,
  Book,
  AlertTriangle
} from "lucide-react";

const categoryIcons = {
  culture: Crown,
  bag_drop: Briefcase,
  starter: Flag,
  marshal: Radio,
  service: Heart,
  standards: Star,
  knowledge: Book,
  safety: AlertTriangle,
};

export default function SOPLibraryPage() {
  const navigate = useNavigate();
  const [sops, setSops] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedSection, setSelectedSection] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sopsRes, progressRes] = await Promise.all([
        apiClient.get("/sops"),
        apiClient.get("/progress"),
      ]);
      setSops(sopsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressForSop = (sopId) => {
    return progress.find((p) => p.sop_id === sopId);
  };

  const sections = [
    { id: "all", label: "All Modules" },
    { id: "foundations", label: "Foundations" },
    { id: "guest_journey", label: "Guest Journey" },
    { id: "excellence", label: "Excellence" },
    { id: "operations", label: "Operations" },
    { id: "expectations", label: "Standards" },
  ];

  const filteredSops = sops.filter((sop) => {
    const matchesSearch = sop.title.toLowerCase().includes(search.toLowerCase()) ||
      sop.description.toLowerCase().includes(search.toLowerCase());
    const matchesSection = selectedSection === "all" || sop.section === selectedSection;
    return matchesSearch && matchesSection;
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-12 w-64"></div>
          <div className="skeleton h-12 w-full"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton h-72"></div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in-up max-w-7xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            Training Modules
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Master the Royal Dornoch standards through comprehensive training.
          </p>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <Input
              placeholder="Search modules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-11 bg-white border-[hsl(var(--border))]"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
            {sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => setSelectedSection(sec.id)}
                className={`px-4 py-2 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedSection === sec.id
                    ? "bg-[var(--rd-green)] text-white"
                    : "bg-white text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]"
                }`}
                data-testid={`filter-${sec.id}`}
              >
                {sec.label}
              </button>
            ))}
          </div>
        </div>

        {/* SOP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSops.map((sop, index) => {
            const sopProgress = getProgressForSop(sop.id);
            const stepsCompleted = sopProgress?.completed_steps?.length || 0;
            const totalSteps = sop.steps?.length || 0;
            const progressPercent = totalSteps > 0 ? Math.round((stepsCompleted / totalSteps) * 100) : 0;
            const isCompleted = sopProgress?.completed;
            const isInProgress = !isCompleted && stepsCompleted > 0;
            const IconComponent = categoryIcons[sop.category] || Flag;

            return (
              <div
                key={sop.id}
                className={`sop-card stagger-${(index % 6) + 1} animate-fade-in-up`}
                onClick={() => navigate(`/sops/${sop.id}`)}
                data-testid={`sop-library-card-${sop.id}`}
              >
                <div className="sop-card-image">
                  <img
                    src={sop.image_url || "https://images.pexels.com/photos/1325706/pexels-photo-1325706.jpeg"}
                    alt={sop.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-sm bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-white/90 text-xs font-medium uppercase tracking-wider">
                        {sop.category.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                  {isCompleted && (
                    <div className="absolute top-3 right-3">
                      <span className="badge-gold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Complete
                      </span>
                    </div>
                  )}
                  {isInProgress && (
                    <div className="absolute top-3 right-3">
                      <span className="badge-green flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        In Progress
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                    {sop.title}
                  </h3>
                  <p className="text-[hsl(var(--muted-foreground))] text-sm mb-4 line-clamp-2">
                    {sop.description}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[hsl(var(--muted-foreground))]">
                        {totalSteps} steps • {sop.quiz_questions?.length || 0} questions
                      </span>
                      <span className="font-medium text-[var(--rd-green)]">{progressPercent}%</span>
                    </div>
                    <div className="rd-progress-track">
                      <div className="rd-progress-fill" style={{ width: `${progressPercent}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredSops.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />
            </div>
            <h3 className="text-xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
              No Modules Found
            </h3>
            <p className="text-[hsl(var(--muted-foreground))]">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
